// @flow
/** @jsx isolateComponent */

import { LogoImage } from '@zapier/common-components';
import { isolateComponent } from '@zapier/common-utils';
import {
  type AccountPlan,
  type TrialExtensionReason,
  trialExtensionReasons,
} from '@zapier/billing/src/types';
import { Button, Colors, Icon, Spacer } from '@zapier/design-system';

import ContentWrapper from './ContentWrapper';

const reasonMessagingProps = ({ appName, canExtendTrial, plan }) => ({
  [trialExtensionReasons.paths]: {
    bodyText: canExtendTrial
      ? `To activate your Zap, you'll need access to Paths. Upgrade now or start a free 7-day trial of Professional for Premium features like Paths and more.`
      : "To activate your Zap, you'll need access to Paths. Edit your Zap or upgrade now to activate.",
    headingText: 'Paths',
  },
  [trialExtensionReasons.multistep]: {
    bodyText: canExtendTrial
      ? `To create a Zap with more than one action, you'll need a Premium plan. Reduce your steps or upgrade for unlimited multi-step Zaps.`
      : "To create a Zap with more than one action, you'll need a Premium plan. Edit your zap or upgrade now to activate.",
    headingText: 'Multi-Step Zap',
    image: <IconImage iconName="miscBolt" />,
  },
  [trialExtensionReasons.premium]: {
    bodyText: canExtendTrial
      ? `To activate your Zap, you'll need access to Premium Apps. Upgrade now or start a free 7-day trial of Professional for unlimited Premium Apps.`
      : "To activate your Zap, you'll need access to Premium Apps. Edit your zap or upgrade now to activate.",
    headingText: 'Premium App',
    image: <IconImage appName={appName} />,
  },
  [trialExtensionReasons.premiumLimit]: {
    bodyText: canExtendTrial
      ? "To activate your Zap, you'll need more Premium Apps. Upgrade now or start a free 7-day trial of Professional for unlimited Premium Apps."
      : "To activate your Zap, you'll need more Premium Apps. Edit your Zap or upgrade now to activate",
    headingText: 'More Premium Apps',
    image: <IconImage appName={appName} />,
  },
  [trialExtensionReasons.zapLimit]: {
    bodyText: canExtendTrial
      ? "To activate your Zap, you'll need more Zaps. Upgrade now or start a free 7-day trial of Professional for unlimited Zaps."
      : "To activate your Zap, you'll need more Zaps. Reduce the number of Zaps you're using or upgrade now to activate.",
    headingText: 'More Zaps',
    image: <IconImage iconName="miscBolt" />,
  },
  [trialExtensionReasons.taskLimit]: {
    bodyText: canExtendTrial
      ? `To activate your Zap, you'll need more Tasks. Upgrade now or start a free 7-day trial of Professional for ${Number(
          plan.limits.action_limit
        ).toLocaleString()} tasks.`
      : "To activate your Zap, you'll need more Tasks. Reduce the number of Zaps you're using or upgrade now to activate.",
    headingText: 'More Tasks',
    image: <IconImage iconName="formAddCircle" />,
  },
  [trialExtensionReasons.unknown]: {
    bodyText: "To activate your Zap, you'll need to upgrade your account.",
    headingText: 'Upgrade Needed',
  },
});

const styles = {
  imageContainer: {
    width: 210,
    height: 210,
    backgroundColor: Colors.grey000,
    boxShadow: '0px 20px 30px rgba(0, 0, 0, 0.1)',
    backdropFilter: 'blur(5px)',
    borderRadius: 60,
    padding: 30,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  image: {
    backgroundColor: 'white',
    padding: 10,
    width: 150,
    height: 150,
    borderRadius: 40,
    border: `1px solid ${Colors.grey200}`,
  },
};

type IconImageProps = {|
  appName?: string,
  iconName?: string,
|};

const IconImage = ({ appName, iconName }: IconImageProps) => {
  return (
    <div css={styles.imageContainer}>
      <div css={styles.image}>
        {appName ? (
          <LogoImage app={appName} />
        ) : iconName ? (
          <Icon name={iconName} size={128} />
        ) : null}
      </div>
    </div>
  );
};

type ReasonProps = {|
  appName?: string,
  canExtendTrial: boolean,
  onClose: () => void,
  onExtendTrial: () => void,
  onUpgradeClick: () => void,
  plan: AccountPlan,
  reason: TrialExtensionReason,
|};

const Reason = ({
  appName,
  canExtendTrial,
  onClose,
  onExtendTrial,
  onUpgradeClick,
  plan,
  reason,
}: ReasonProps) => {
  const contentProps = {
    ...reasonMessagingProps({ appName, canExtendTrial, plan })[
      reason || trialExtensionReasons.unknown
    ],
    buttons: [
      <Button
        casing="uppercase"
        hasShadow={true}
        key="upgrade"
        onClick={() => onUpgradeClick()}
      >
        Upgrade Now
      </Button>,
      <Spacer key="space" width={10} />,
      canExtendTrial ? (
        <Button
          casing="uppercase"
          color="greyAlt"
          key="extend"
          onClick={onExtendTrial}
        >
          Start Trial
        </Button>
      ) : (
        <Button
          casing="uppercase"
          color="greyAlt"
          key="close"
          onClick={onClose}
        >
          Back
        </Button>
      ),
    ],
    subtitleText: reason ? 'Premium Feature' : null,
  };
  return <ContentWrapper {...contentProps} />;
};

export default Reason;
